import { inject } from '@angular/core';
import { CanActivateFn, CanDeactivateFn, Router, UrlTree } from '@angular/router';
import { OrderJSON } from '@app/@shared/orderjson.model';
import { SessionStorageService } from '@app/sessionStorage.service';
import { isIncompleteResubmission } from '@app/@shared/order-resubmission';

function isAnyAgreementSigned(sessionStorageService: SessionStorageService): boolean {
  const order = sessionStorageService.getAllData() as OrderJSON;
  return !!(order?.isNRSPAYServiceAgrementSigned || order?.isPlatFormServiceAgrementSigned);
}

// Signed orders sent back as "incomplete" stay editable so the user can correct and resubmit them.
function hasCompletedOrder(sessionStorageService: SessionStorageService): boolean {
  const completedOrder = sessionStorageService.getAllData(SessionStorageService.EXISTING_ORDER_FLOW);
  return !!completedOrder && Object.keys(completedOrder).length > 0;
}

function isResubmissionUnlocked(sessionStorageService: SessionStorageService): boolean {
  return (
    isIncompleteResubmission(sessionStorageService.getAllData() as OrderJSON) &&
    !hasCompletedOrder(sessionStorageService)
  );
}

// Blocks browser Back/Forward (popstate) once any agreement is signed; in-app navigation is unaffected.
export const signedAgreementBackGuard: CanDeactivateFn<unknown> = (): boolean => {
  if (inject(Router).getCurrentNavigation()?.trigger !== 'popstate') {
    return true;
  }
  const sessionStorageService = inject(SessionStorageService);
  return !isAnyAgreementSigned(sessionStorageService) || isResubmissionUnlocked(sessionStorageService);
};

// Blocks direct URL entry into order steps before Agreements once any agreement is signed.
export const signedAgreementLockGuard: CanActivateFn = (): boolean | UrlTree => {
  const sessionStorageService = inject(SessionStorageService);
  if (!isAnyAgreementSigned(sessionStorageService) || isResubmissionUnlocked(sessionStorageService)) {
    return true;
  }

  const target = hasCompletedOrder(sessionStorageService) ? '/account-portal' : '/agreements';
  return inject(Router).createUrlTree([target]);
};

// Success is only reachable after an agreement has been signed.
export const successPageGuard: CanActivateFn = (): boolean | UrlTree => {
  return isAnyAgreementSigned(inject(SessionStorageService)) || inject(Router).createUrlTree(['/agreements']);
};
